import * as Sentry from '@sentry/browser'
import { AxiosResponse } from 'axios'

import { ConversationAPI, TopicTrainingAPI } from 'api'

export enum TopicTrainingLocalStorage {
  PUBLISH_SUCCESS = 'AITopicPublishSuccessAt',
  PUBLISH_ERROR_DISMISSED = 'AITopicPublishErrorDismissedAt',
}

export enum PublishStatus {
  ARCHIVED = 'archived',
  DRAFT = 'draft',
  IN_PROGRESS = 'inProgress',
  SUCCESS = 'success',
  PUBLISHED = 'published',
  UNPUBLISHED = 'unpublished',
  UNPUBLISHED_ARCHIVE = 'unpublishedArchive',
  UNPUBLISHED_RESTORE = 'unpublishedRestore',
  ERROR = 'error',
}

export enum SuggestedNovelExampleAction {
  AUGMENT_TOPIC = 'AUGMENT_TOPIC',
  CREATE_TOPIC = 'CREATE_TOPIC',
}
export interface TopicSummary {
  topicId: string
  topicName: string
  replyText?: string
  exampleCount: number
  isDriftManaged: boolean
  isArchived: boolean
  topicPublicUIDescription?: string
  topicPublicUIExamples?: string
  updatedAt: number
  variableReplyCount: number
  publishedAt?: number
  publishStatus?: Exclude<
    PublishStatus,
    PublishStatus.SUCCESS | PublishStatus.ERROR
  >
  topicGroupId?: string
  topicGroupName?: string
}

export interface NovelExampleMetaData {
  utteredAt: number
  utteredBy: number
  conversationId: number
  messageId: number
  playbookId: number
}

export interface NovelExample {
  id: string
  text: string
  suggestedTopicId?: string
  suggestedTopicLabel?: string
  exampleMeta: NovelExampleMetaData
  suggestedUtteranceSource?: string
  suggestedAction?: SuggestedNovelExampleAction
  dismissed?: boolean
  addedToTopic?: boolean
  isNewTopic?: boolean
}

export interface NovelExampleGroup {
  suggestedTopicLabel?: string
  novelExamples: NovelExample[]
}

export interface Topic {
  topicId: string
  replyText: string
  examples: string[]
  topicName: string
  topicPublicUIDescription?: string
  topicPublicUIExamples?: string
  driftManaged?: boolean
  variableReplies: VariableReply[]
  topicGroupId?: string
}

export interface CreateTopicPayload {
  topicName: string
  replyText?: string
  variableReplies?: VariableReply[]
  examples: string[]
  novelExampleIds?: string[]
  topicGroupId?: string
}

export interface EditTopicPayload {
  topicName: string
  replyText?: string
  variableReplies?: VariableReply[]
  topicGroupId?: string
}

export interface TopicExample {
  id: string
  text: string
  topicId: string
  createdAt: number
  updatedAt: number
}

export interface VariableReply {
  id?: string
  keywords: string[]
  text: string
  priority: number
  createdAt?: number
  updatedAt?: number
}

export interface TopicsPublishStatus {
  publishStatus?:
    | PublishStatus.IN_PROGRESS
    | PublishStatus.SUCCESS
    | PublishStatus.ERROR
  publishedAt?: number
  orgId?: string
  erroredAt?: number
}

export interface TopicGroup {
  id: string
  name: string
}

export interface Playbook {
  playbookName: string
  playbookId: string
  playbookStatus: string
}

const formatResponse = <T>(response: AxiosResponse<T>) => response.data
// Log errors to Sentry
export const captureAIException = (
  error: Error,
  extra: { [k: string]: unknown }
) =>
  Sentry.captureException(error, {
    tags: {
      'ai-topics': true,
    },
    ...extra,
  })

// Intercept errors for logging
TopicTrainingAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    const request = error.config
    const response = error.response?.data
    captureAIException(error, {
      url: request?.url || '',
      method: request?.method || '',
      ...response,
    })
    return Promise.reject(error)
  }
)

export const fetchAllTopics = (orgId: number) =>
  TopicTrainingAPI.get<{ topics: TopicSummary[] }>(`/topics/org/${orgId}`).then(
    formatResponse
  )

export const updateTopicReply = (topicId: string, replyText?: string) =>
  TopicTrainingAPI.patch(`/replies/${topicId}`, { replyText })

export const archiveTopic = (topicId: string, orgId: number) =>
  TopicTrainingAPI.post(`/topics/${orgId}/${topicId}/archive`, {
    archive: true,
  })

export const restoreTopic = (topicId: string, orgId: number) =>
  TopicTrainingAPI.post(`/topics/${orgId}/${topicId}/archive`, {
    archive: false,
  })

export const getAITopicsEnabled = (playbookId: number, orgId: number) =>
  ConversationAPI.get<{ enabled: boolean }>(
    `playbooks/settings/all_other_responses/${orgId}/${playbookId}`
  )

export const changeAITopicsEnabledStatus = (
  playbookId: number,
  orgId: number,
  enabled: boolean
) =>
  ConversationAPI.patch(
    `playbooks/settings/all_other_responses/${orgId}/${playbookId}`,
    { enabled: enabled }
  )

/**
 * @deprecated */
export const createTopic = (orgId: number, topic: CreateTopicPayload) =>
  TopicTrainingAPI.post(`/topics/${orgId}`, { ...topic })

export const createTopicV2 = (orgId: number, topic: CreateTopicPayload) =>
  TopicTrainingAPI.post(`/topics/${orgId}/v2`, { ...topic })

export const fetchTopic = (orgId: number, topicId: string) =>
  TopicTrainingAPI.get<Topic>(`/topics/${orgId}/${topicId}`)

export const editTopic = (
  orgId: number,
  topicId: string,
  modifiedTopic: EditTopicPayload
) =>
  TopicTrainingAPI.patch(`/topics/${orgId}/${topicId}`, {
    ...modifiedTopic,
  })

export const fetchTopicExamples = (topicId: string) =>
  TopicTrainingAPI.get<{ examples: TopicExample[] }>(
    `/topics/examples/${topicId}?archived=false`
  )

export const addTopicExamples = (topicId: string, examples: string[]) =>
  TopicTrainingAPI.post(`/topics/examples/${topicId}`, {
    topicId,
    examples,
  })

export const updateTopicExamples = (
  orgId: number,
  examples: { [key: string]: string }
) =>
  TopicTrainingAPI.patch(`/topics/examples/${orgId}/update-many`, {
    examples: Object.keys(examples).map((id: string) => ({
      id,
      text: examples[id],
    })),
  })

export const archiveTopicExample = (exampleId: string) =>
  TopicTrainingAPI.post(`/topics/examples/${exampleId}/archive`, {
    archive: true,
  })

export const archiveTopicExamples = (orgId: number, exampleIds: string[]) =>
  TopicTrainingAPI.post(`/topics/examples/${orgId}/archive-many`, {
    exampleIds,
  })

export const fetchNovelExamples = (orgId: number) =>
  TopicTrainingAPI.get(`/novel_examples/org/${orgId}`)

export const createNovelTopicFromExamples = (
  orgId: number,
  topic: CreateTopicPayload
) => TopicTrainingAPI.post(`/topics/${orgId}/v2`, { ...topic })

export const addNovelExamplesToTopic = (
  topicId: string,
  novelExampleIds: NovelExample['id'][]
) =>
  TopicTrainingAPI.patch(`/novel_examples/topics/${topicId}`, {
    topicId,
    novelExampleIds,
  })

export const dismissNovelExample = (exampleId: string) =>
  TopicTrainingAPI.post(`/novel_examples/dismiss/${exampleId}`)

export const dismissBulkNovelExamples = (orgId: number, exampleIds: string[]) =>
  TopicTrainingAPI.post(`/novel_examples/dismiss-many/${orgId}`, {
    orgId,
    novelExampleUUIDs: exampleIds,
  })

export const validateTopicName = (
  orgId: number,
  topicName: string,
  topicId?: string
) =>
  TopicTrainingAPI.post(
    `/validate/topic-name/${orgId}`,
    topicId
      ? {
          topicName,
          topicId,
        }
      : { topicName }
  )

export const publishTopics = (orgId: number, datasetId: string) =>
  TopicTrainingAPI.post(
    `/datasets/training/${orgId}/dataset/${datasetId}/request`,
    {
      orgId,
      datasetIdentifier: orgId,
    }
  ).then(formatResponse)

export const fetchTopicsPublishStatus = (orgId: number, datasetId: string) =>
  TopicTrainingAPI.get(
    `/datasets/training/${orgId}/dataset/${datasetId}/status`
  ).then(formatResponse)

export const fetchTopicGroups = (orgId: number) =>
  TopicTrainingAPI.get<{ topicGroups: TopicGroup[] }>(
    `/topics/org/${orgId}/groups`
  ).then(formatResponse)

export const createTopicGroup = (orgId: number, name: string) =>
  TopicTrainingAPI.post<string>(`/topics/org/${orgId}/groups`, {
    name,
  }).then(formatResponse)

export const fetchPlaybooksForTopic = async (orgId: number, topicId: string) =>
  ConversationAPI.get<Playbook[]>(
    `playbooks/topics/${orgId}/containing_topic/${topicId}`
  )
